import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BulkSelectSurveyParticipantsDialogComponent } from '@app/components/dialogs/bulk-select-survey-participants-dialog/bulk-select-survey-participants-dialog.component';
import { Columns as Column, Row } from '@app/components/simple-table/simple-table.component';
import { AnalyticEvents } from '@app/enums';
import { selectedEmployeesHaveChanged } from '@app/functions';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { BaseForm } from '@forms/base.form';
import { Employee } from '@models/employee/employee.model';
import { Survey } from '@models/performance/survey.model';

const mapEmployeesToRow = (employee: Employee): Row => {
    return {
        cells: [
            { employee, isAvatar: true },
            employee.department?.name,
            employee.manager?.fullName,
            employee.position?.title,
        ],
    };
};

@Component({
    selector: 'app-form-survey-participants',
    templateUrl: './survey-participants.template.html',
    styleUrls: ['./survey-participants.style.scss'],
})
export class SurveyParticipantsForm extends BaseForm {
    @Input() survey: Survey;
    @Input() participants: Map<number, boolean> = new Map<number, boolean>();
    @Output() participantsChanged: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('selectParticipants', { static: true }) selectParticipants: BulkSelectSurveyParticipantsDialogComponent;

    isLoading = false;
    employees: Employee[] = [];
    managerFilter: Employee | null = null;

    columns: Column[] = [
        { columnName: 'surveys.store.selectedParticipantsColumns.participant', columnSpan: 'columns-3' },
        { columnName: 'surveys.store.selectedParticipantsColumns.department', columnSpan: 'columns-3' },
        { columnName: 'surveys.store.selectedParticipantsColumns.reportsTo', columnSpan: 'columns-3' },
        { columnName: 'surveys.store.selectedParticipantsColumns.position', columnSpan: 'columns-3' },
    ];

    rows: Row[] = [];

    constructor(
        private auth: AuthService,
        private readonly analyticService: AnalyticService
    ) {
        super();
    }

    get valid(): boolean {
        return this.participants.size > 0;
    }

    selectAll(): void {
        this.employees.forEach((employee) => this.participants.set(employee.id, true));
        this.participantsChanged.emit();
    }

    deselectAll(): void {
        this.employees.forEach((employee) => this.participants.set(employee.id, false));
        this.participantsChanged.emit();
    }

    async onManagerFilter(): Promise<void> {
        this.isLoading = true;
        this.employees = await this.getEmployees();
        this.isLoading = false;
    }

    openParticipantSelect(): void {
        this.selectParticipants.show<Employee[] | null>(this.employees).then((participants) => {
            if (!Array.isArray(participants)) {
                participants = this.employees;
            }

            if (selectedEmployeesHaveChanged(this.employees, participants)) {
                this.analyticService.trackEvent(AnalyticEvents.SurveysCreateSurveySelectOrRemoveParticipants);
            }

            this.employees = participants;
            this.rows = this.employees.map(mapEmployeesToRow);

            this.participants.clear();
            participants.forEach((participant) => this.participants.set(participant.id, true));

            this.participantsChanged.emit();
        });
    }

    updateSelectedParticipants(selection): void {
        this.employees = selection;
        this.participantsChanged.emit();
    }

    onSearch(searchQuery: string | null): void {
        if (searchQuery === null) {
            this.rows = this.employees.map(mapEmployeesToRow);
            return;
        }

        const nameIncludesSearchQuery = (employee: Employee): boolean =>
            employee.fullName.toLowerCase().includes(searchQuery.toLocaleLowerCase());

        this.rows = this.employees.filter(nameIncludesSearchQuery).map(mapEmployeesToRow);
    }

    private async getEmployees(): Promise<Employee[]> {
        const query = Employee.param('company', this.auth.company.id).where('status', 'active').limit(0);

        if (this.managerFilter) {
            query.where('reportsTo', this.managerFilter.id);
        }

        const [employees] = await query.get();

        return employees;
    }
}
